import { ThemeProvider } from '@ori-ui/mui';
import { doDevelopmentLogin, UniversalTokenProvider } from '@ori/auth';
import type { PickClientAppDataConfigProps } from '@ori/client-app-config';
import { parseClientAppConfig } from '@ori/client-app-config';
import { createRoot } from 'react-dom/client';

import { App } from './App';
import type { AppProps as AppConfig } from './App';
import { EMOTION_CACHE_KEY } from './constants';
import { ErrorBoundary } from './containers/ErrorBoundary';

/**
 * React app renderer that is used when app is used as a standalone app
 */
export async function render(rootElement: HTMLElement) {
  const { config = '' } = rootElement.dataset;
  const { identityUrl, ...appConfig } = parseClientAppConfig<
    AppConfig & PickClientAppDataConfigProps<'customerId' | 'identityUrl'>
  >(config, ['tenant', 'currentLanguage', 'graphqlUrl', 'staticApiUrl', 'globalApiUrl']);
  const { customerId, tenant } = appConfig;

  // Get token for local development
  if (process.env.NODE_ENV !== 'production') {
    // when using Requestly we do not want to load token
    if (process.env.REACT_APP_USE_REQUESTLY === '1') {
      // eslint-disable-next-line no-console -- we need to log this info
      console.log('USING REQUESTLY');
    } else if (process.env.REACT_APP_ANONYMOUS_MODE !== '1') {
      await doDevelopmentLogin({ identityUrl, tenant });
    }
  }

  createRoot(rootElement).render(
    <ErrorBoundary>
      <UniversalTokenProvider
        customerId={customerId}
        initialToken=""
      >
        <ThemeProvider
          enableCacheProvider
          cacheKey={EMOTION_CACHE_KEY}
        >
          <App {...appConfig} />
        </ThemeProvider>
      </UniversalTokenProvider>
    </ErrorBoundary>,
  );
}
